import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import isFunction from 'lodash/isFunction';
import sum from 'lodash/sum';
import isUndefined from 'lodash/isUndefined';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

// GLOBAL VARIABLES
import { SORT_ORDER } from 'globals.js';

// LOCAL VARIABLES
import { DEFAULT_ACTION_ITEM_WIDTH, PLACEHOLDER, ROW_HEIGHT } from '../variables';

// HELPERS
import { useListData } from 'helpers/getListData';
import usePrevious from 'helpers/usePrevious';
import useResizeContainer from '../helpers/useResizeContainer';

// LOCAL COMPONENTS
import ActionsCell from '../tools/ActionsCell';
import LinkCell from '../tools/LinkCell';
import NumberCell from '../tools/NumberCell';
import TableHeader from './layout/TableHeader';
import TableBody from './layout/TableBody';

// STYLES
import styles from './table.module.scss';

export default function Table({
  autoSizeHeight = true, //
  className,
  disableResizing,
  onSort,
  placeholder = PLACEHOLDER,
  rowHeight = ROW_HEIGHT,
  showNumber,
  ...props
}) {
  // REFS
  const outerContainerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const nextPageLoadingRef = useRef(false);

  // HOOKS / PROPS
  const {
    actions, //
    columns: rawColumns,
    data: { start_row, startRow = start_row } = {},
    loading,
    preferences: { filters, showColumns } = {},
    result: data = [],
    sortable,
    updateContext,
    updateFilter
  } = Object.assign(useListData() || {}, props);

  const containerHeight = useResizeContainer(outerContainerRef, autoSizeHeight);
  
  const useClientSort = isFunction(onSort);

  // MEMOS
  const columns = useMemo(() => {
    let transformed = rawColumns.map(({ component, label, key, sortable: isColumnSortable, to, width, ...c }) => {
      const column = {
        ...c,
        header: label,
        id: key,
        accessorKey: key,
        size: width,
        to
      };
      if (component) column.cell = component;
      else if (to) column.cell = LinkCell;
      if (sortable || useClientSort) column.enableSorting = isColumnSortable !== false;
      column.enableHiding = true;
      return column;
    });

    if (showNumber) {
      const rowNumberColumn = {
        header: '#',
        id: 'row',
        headerClassName: 'flex-grow-0',
        className: 'flex-grow-0',
        cell: ({ row: { index = 0 } }) => <NumberCell className="text-left" value={index + 1} />,
        size: 45
      };

      transformed = [rowNumberColumn, ...transformed];
    }

    if (actions?.length) {
      const actionsColumn = {
        header: '',
        id: 'actions',
        headerClassName: 'flex-grow-0',
        className: 'flex-grow-0',
        cell: p => <ActionsCell {...p} column={{ actions }} />,
        size: sum(actions.map(a => a.width || DEFAULT_ACTION_ITEM_WIDTH)),
        enableResizing: false
      };
      transformed = [...transformed, actionsColumn];
    }

    if (transformed.length > 2) {
      // Ensure no resizing last column
      transformed[transformed.length - 1].enableResizing = false;
    }

    return transformed;
  }, [actions, rawColumns, showNumber, sortable, useClientSort]);

  const columnVisibility = useMemo(() => {
    const visible = {};
    columns.forEach(c => {
      if (c.accessorKey) {
        visible[c.accessorKey] = showColumns ? showColumns.includes(c.accessorKey) : true;
      }
    });
    return visible;
  }, [columns, showColumns]);

  // STATE
  const [sorting, setSorting] = useState([{ id: filters?.sort, desc: filters?.order === 'D' }]);

  const orderedData = useMemo(() => {
    if (useClientSort && sorting?.[0]?.id) {
      const sorted = onSort(data, sorting[0].id, sorting[0].desc ? 'desc' : 'asc');
      return sorted;
    }
    return data;
  }, [data, onSort, sorting, useClientSort]);

  // REACT TABLE
  const table = useReactTable({
    columns,
    columnResizeDirection: 'ltr',
    columnResizeMode: 'onChange',
    data: orderedData || [],
    enableSorting: sortable,
    enableSortingRemoval: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    onSortingChange: s => {
      const sort = s()?.map(({ id, desc }, i) => {
        const descState = sorting?.[i]?.desc;
        return {
          id,
          desc: id === sorting?.[0]?.id && !isUndefined(descState) ? !descState : desc
        };
    });
      setSorting(sort);
    },
    initialState: {
      columnVisibility
    },
    state: {
      columnVisibility,
      sorting
    },
  });

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight,
    getScrollElement: () => tableContainerRef.current,
    measureElement: typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
      ? element => element?.getBoundingClientRect().height
      : undefined,
    overscan: 5,
  });

  const hasNextPage = !!startRow;

  // CALLBACKS
  const loadPage = useCallback(
    async containerRefElement => {
      if (!hasNextPage || nextPageLoadingRef.current) return;
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - 50 <= clientHeight) {
          nextPageLoadingRef.current = true;
          updateContext({ type: 'paginate' });
        } 
      }
    },
    [hasNextPage, updateContext],
  );

  const debouncedLoadPage = debounce(loadPage, 500);

  const handleSort = useCallback(
    ([{ id, desc } = {}]) => {
      const order = {
        [true]: SORT_ORDER.descending,
        [false]: SORT_ORDER.ascending
      }[desc];

      updateFilter({ sort: id, order });
    },
    [updateFilter]
  );

  // EFFECTS
  useEffect(() => {
    if (!loading) nextPageLoadingRef.current = false;
  }, [loading]);

  useEffect(() => {
    if (sortable && !useClientSort) handleSort(sorting);
  }, [handleSort, sortable, sorting, useClientSort]);

  const prevDataLength = usePrevious(data.length);

  // If resetting list (on sort or filter), scroll to top
  useEffect(() => {
    if (tableContainerRef.current && prevDataLength && data.length < prevDataLength) {
      tableContainerRef.current.scrollTo({
        top: 0,
        behavior: 'instant',
      })
    }
  }, [prevDataLength, data.length]);

  return (
    <div ref={outerContainerRef} className={clsx('position-relative d-flex flex-column', styles.container, className)}>
      <div
        ref={tableContainerRef}
        style={{ height: containerHeight, overflow: 'auto' }}
        onScroll={e => debouncedLoadPage(e.target)}
      >
        <table className="table table-striped flex-grow-1 w-100 mb-0">
          <TableHeader groups={table.getHeaderGroups()} />
          <TableBody
            {...props}
            rows={table.getRowModel().rows}
            rowVirtualizer={rowVirtualizer}
            rowHeight={rowHeight}
            placeholder={placeholder}
          />
        </table>
      </div>
    </div>
  );
}
